import React, { useContext, useEffect, useState } from 'react'
import { EmployeeContext } from '../../admin/personnel/employeeSingle/employeeSingle'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import moment from 'moment'

import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import {
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import SchoolIcon from '@mui/icons-material/School'
import BackupIcon from '@mui/icons-material/Backup'
import BigModal from '../../../components/Modals/BigModal'
import Popup from './Popup'
import CourseCard from './CourseCard'
import EmptyState from '../../../components/EmptyState/EmptyState'
import { getMyCourses } from '../../../training/helpers/trainingApi'

const MyMandatoryTraining = () => {
  const [loading, setLoading] = useState(true)
  const [coursesLoading, setCoursesLoading] = useState(true)
  const [allCourses, setAllCourses] = useState([])
  const [mandatoryTraining, setMandatoryTraining] = useState([])
  const { user } = useContext(UserContext)
  // The user in context is only loaded when the app boots, so a clinical role
  // set on the employee record since then is missing from it (the page showed
  // "no clinical role set" until a full reload). /mymandatorytraining reads
  // the role straight off the employee record, so that answer wins once it
  // arrives.
  const [clinicalRole, setClinicalRole] = useState(
    user?.employee?.clinicalRole
  )
  const [selectedCourse, setSelectedCourse] = useState(null)
  // E-learning info per course id, from /training/my-courses. Empty when the
  // organisation has no e-learning (the route answers 403 below Premium).
  const [eLearning, setELearning] = useState({ byCourse: {}, assessor: false })

  const getELearning = () =>
    getMyCourses()
      .then((res) => {
        if (res?.status !== 200 || !Array.isArray(res.data?.courses)) return
        setELearning({
          assessor: !!res.data.assessor,
          byCourse: Object.fromEntries(
            res.data.courses.map((entry) => [String(entry.courseId), entry]),
          ),
        })
      })
      .catch(() => {})

  useEffect(() => {
    getELearning()
  }, [])

  useEffect(() => {
    API.get('/mandatorytraininglist')
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setAllCourses(res.data)
        }
      })
      .finally(() => setCoursesLoading(false))
  }, [])

  const getMyMandatoryTraining = () => {
    API.get(`/mymandatorytraining`)
      .then((res) => {
        if (res.status === 200) {
          setMandatoryTraining(res.data?.mandatoryTraining ?? [])
          if (res.data?._id) {
            setClinicalRole(res.data.clinicalRole)
          }
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getMyMandatoryTraining()
  }, [])

  // Course reviewers see every e-learning course, whatever their role.
  const listOfCourses = allCourses.filter(
    (course) =>
      course?.requiredBy?.[clinicalRole] ||
      (eLearning.assessor && eLearning.byCourse[String(course._id)])
  )

  const canConfigureTraining =
    user?.security?.isSiteAdmin || user?.employee?.teams?.includes('Personnel')
  const hasClinicalRole = !!clinicalRole

  return (
    <Stack gap={2}>
      <Typography variant="title2">My Mandatory Training</Typography>
      {loading || coursesLoading ? (
        <LinearProgress />
      ) : listOfCourses.length === 0 ? (
        <EmptyState
          icon={SchoolIcon}
          title={
            hasClinicalRole
              ? 'No training assigned to your role yet'
              : 'Your account has no clinical role set'
          }
          description={
            hasClinicalRole
              ? `Mandatory training courses appear here once your organisation has configured which courses the ${clinicalRole} role requires and how often they renew.`
              : 'Mandatory training is assigned by clinical role. Once a role is set on your employee profile, the courses required for that role will appear here.'
          }
          steps={
            canConfigureTraining
              ? [
                  'Open Site Configuration → Mandatory Training.',
                  'Add each course your organisation requires and its renewal period.',
                  'Tick the clinical roles each course applies to — staff then see their courses here and compliance is tracked automatically.',
                ]
              : undefined
          }
          actions={
            canConfigureTraining
              ? [
                  {
                    label: 'Configure Mandatory Training',
                    to: '/site-configuration/mandatory-training',
                  },
                  ...(hasClinicalRole
                    ? []
                    : [{ label: 'Open Employees', to: '/employees' }]),
                ]
              : []
          }
          sx={{ boxSizing: 'border-box' }}
        />
      ) : (
        <Grid container spacing={2}>
          {listOfCourses.map((course) => (
            <CourseCard
              key={course._id}
              course={course}
              mandatoryTraining={mandatoryTraining}
              eLearning={eLearning.byCourse[String(course._id)]}
              setSelectedCourse={setSelectedCourse}
            />
          ))}
        </Grid>
      )}
      <Popup
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        getMyMandatoryTraining={getMyMandatoryTraining}
        assessor={eLearning.assessor}
      />
    </Stack>
  )
}

export default MyMandatoryTraining
